import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import type { LessonSession } from './types';
import styles from './ProgressCharts.module.css';

interface AnswerBreakdownChartProps {
  sessions: LessonSession[];
}

interface BreakdownData {
  name: string;
  value: number;
  color: string;
  percentage: number;
  [key: string]: string | number;
}

const COLORS = {
  correct: '#22c55e',
  incorrect: '#ef4444',
  outOfTime: '#f59e0b',
  missed: '#94a3b8',
};

export function AnswerBreakdownChart({ sessions }: AnswerBreakdownChartProps) {
  const { chartData, total } = useMemo(() => {
    const totals = sessions.reduce(
      (acc, session) => ({
        correct: acc.correct + session.correct_count,
        incorrect: acc.incorrect + session.incorrect_count,
        outOfTime: acc.outOfTime + session.out_of_time_count,
        missed: acc.missed + session.missed_count,
      }),
      { correct: 0, incorrect: 0, outOfTime: 0, missed: 0 }
    );

    const totalAnswers = totals.correct + totals.incorrect + totals.outOfTime + totals.missed;

    const data: BreakdownData[] = [
      {
        name: 'Correct',
        value: totals.correct,
        color: COLORS.correct,
        percentage: totalAnswers > 0 ? (totals.correct / totalAnswers) * 100 : 0,
      },
      {
        name: 'Incorrect',
        value: totals.incorrect,
        color: COLORS.incorrect,
        percentage: totalAnswers > 0 ? (totals.incorrect / totalAnswers) * 100 : 0,
      },
      {
        name: 'Out of Time',
        value: totals.outOfTime,
        color: COLORS.outOfTime,
        percentage: totalAnswers > 0 ? (totals.outOfTime / totalAnswers) * 100 : 0,
      },
      {
        name: 'Missed',
        value: totals.missed,
        color: COLORS.missed,
        percentage: totalAnswers > 0 ? (totals.missed / totalAnswers) * 100 : 0,
      },
    ].filter((d) => d.value > 0);

    return { chartData: data, total: totalAnswers };
  }, [sessions]);

  if (sessions.length === 0 || total === 0) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>Answer Breakdown</h3>
        </div>
        <div className={styles.emptyState}>No data yet</div>
      </div>
    );
  }

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>Answer Breakdown</h3>
        <p className={styles.chartSubtitle}>{total.toLocaleString()} total</p>
      </div>
      <div className={styles.pieChartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value, name, props) => {
                const payload = props.payload as BreakdownData;
                return [
                  `${Number(value).toLocaleString()} (${payload.percentage.toFixed(1)}%)`,
                  name,
                ];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className={styles.breakdownLegend}>
        {chartData.map((item) => (
          <div key={item.name} className={styles.breakdownItem}>
            <span className={styles.legendDot} style={{ backgroundColor: item.color }} />
            <span className={styles.breakdownLabel}>{item.name}</span>
            <span className={styles.breakdownValue}>{item.percentage.toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
